import type { AnnouncementPreview } from "@brookbush/types";

import type { GetStaticProps } from "next";
import Link from "next/link";
import useSWR, { type SWRResponse } from "swr";

import Container from "~/components/Container";
import DottedBackground from "~/components/DottedBackground";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.server";

const AnnouncementsPage: NextPageWithProps = () => {
	const { data: announcements } = useSWR("/announcements") as SWRResponse<
		AnnouncementPreview[]
	> & {
		data: AnnouncementPreview[];
	};

	return (
		<DottedBackground>
			<Container className="!max-w-3xl !py-6 tablet:!py-12 flex flex-col gap-8 tablet:gap-12">
				<div className="mx-auto flex flex-col gap-2 tablet:gap-6 text-center">
					<h1 className="font-bold tablet:text-4xl text-2xl">Announcements</h1>

					<p className="tablet:text-lg">
						Latest news and updates about Brookbush Institute will be posted
						here. Stay tuned for exciting announcements, feature releases, and
						community events!
					</p>
				</div>

				<div className="flex flex-col items-center gap-6">
					{announcements?.map((announcement) => (
						<Link
							key={announcement._id}
							href={`/announcements/${announcement.slug}`}
							className="flex w-full flex-col gap-1 tablet:gap-2 rounded-2xl border px-4 tablet:px-5 py-2.5 tablet:py-4 shadow-slate-100 shadow-xl transition hover:bg-slate-100"
						>
							<h2 className="font-semibold tablet:text-2xl text-lg">
								{announcement.title}
							</h2>

							<p>
								{new Date(announcement._createdAt).toLocaleDateString("en-US", {
									dateStyle: "medium",
								})}
							</p>
						</Link>
					))}
				</div>
			</Container>
		</DottedBackground>
	);
};

export const getStaticProps: GetStaticProps = async () => {
	const announcements = await fetcher("/announcements");

	return {
		props: {
			fallback: {
				"/announcements": announcements,
			},
			breadcrumbs: [{ name: "Announcements", href: "/announcements" }],
		},
		revalidate: 60,
	};
};

export default AnnouncementsPage;
